import { Table, Spacer, Flex, Message, withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import type { ITableProps } from '@oliasoft-open-source/react-ui-library';
import translations from 'src/internationalisation/translation-map.json';
import { useTranslation } from 'react-i18next';
import { IActiveCampaign } from 'src/common/interfaces/campaigns.interfaces';
import { SimulationChartType } from 'src/enums/campaigns';
import { shownCumulativeProbabilities } from 'src/views/simulations/simulations';
import { generateTableRows } from '../utils/utils';

interface SummaryTableProps {
  campaign: IActiveCampaign;
  probability: string | number;
  chartType: string;
  dataEntity: number;
  primaryCurrency: string;
  depthUnit: string;
}

const RawSummaryTable = ({ campaign, chartType, dataEntity, primaryCurrency, depthUnit }: SummaryTableProps) => {
  const { t } = useTranslation();

  const subHeaderTranslationsMap: { m: string; ft: string } = {
    m: translations.meters,
    ft: translations.feet,
  };

  const tableSubHeaderName = (() => {
    let name = '';
    if (chartType === SimulationChartType.METERS_PER_DAY) {
      name = t(subHeaderTranslationsMap[depthUnit as keyof typeof subHeaderTranslationsMap]);
    } else if (dataEntity === 0) {
      name = t(translations.days);
    } else {
      name = primaryCurrency;
    }
    return <span className="table-subheader-name">{name}</span>;
  })();

  const generateTableHeaders = (chartType: string) => {
    const showAdjustedColumns =
      (chartType === SimulationChartType.METERS_PER_DAY || chartType === SimulationChartType.TOTAL_DAYS) &&
      campaign.learningCurve;
    const probabilityHeaders = ['Mean', 'P10', 'P50', 'P90'].flatMap((probability) =>
      showAdjustedColumns
        ? [{ value: probability }, { value: `${t(translations.adjusted)} ${probability}` }]
        : [{ value: probability }],
    );

    switch (chartType) {
      case SimulationChartType.METERS_PER_DAY:
      case SimulationChartType.TOTAL_DAYS:
        return [
          {
            cells: [
              {
                value: t(translations.estimate),
                style: { width: showAdjustedColumns ? '28%' : '50%' },
              },
              ...probabilityHeaders,
            ],
          },
          {
            cells: [{ value: '' }, ...probabilityHeaders.map(() => ({ value: tableSubHeaderName }))],
          },
        ];
      case SimulationChartType.CUMULATIVE_PROBABILITY:
        return [
          {
            cells: [
              { value: '' },
              ...shownCumulativeProbabilities?.map((item) => ({
                value: `P${item + 1}`,
              })),
            ],
          },
          {
            cells: [
              { value: '' },
              ...shownCumulativeProbabilities?.map(() => ({
                value: tableSubHeaderName,
              })),
            ],
          },
        ];
      default:
        return [];
    }
  };

  const table = {
    testId: 'campaigns-table-result',
    fixedWidth: '90%',
    headers: generateTableHeaders(chartType),
    rows: generateTableRows(
      chartType,
      dataEntity,
      campaign,
      t,
      translations,
      depthUnit,
    ) as ITableProps['table']['rows'],
  };
  return (
    <>
      <Table table={table} />
      {!campaign.campaignTotal.simulation && (
        <>
          <Spacer />
          <Flex direction="column">
            <Message
              message={{
                content: t(translations.campaigns_campaignTotalIsLoading),
                type: 'Info',
                visible: true,
              }}
            />
          </Flex>
        </>
      )}
    </>
  );
};

export const SummaryTable = withErrorBoundary(RawSummaryTable);
